import { t, Trans } from "@lingui/macro";
import { ArrowRight, Sparkle, X } from "@phosphor-icons/react";
import { Button } from "@stylingcv/ui";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

type PublicResumeCTAProps = {
  templateName?: string;
};

/**
 * Top banner CTA for public resume pages
 * Dismissible with localStorage persistence
 */
export const TopBannerCTA = ({ templateName }: PublicResumeCTAProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const STORAGE_KEY = "stylingcv_public_banner_dismissed";

  useEffect(() => {
    // Check if banner was previously dismissed
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      setIsVisible(true);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    localStorage.setItem(STORAGE_KEY, "true");
  }, []);

  if (!isVisible) return null;

  const ctaUrl = templateName
    ? `/auth/register?source=public-resume&template=${encodeURIComponent(templateName)}`
    : "/auth/register?source=public-resume";

  return (
    <div className="fixed inset-x-0 top-0 z-50 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 px-4 py-2.5 shadow-lg print:hidden">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex flex-1 items-center justify-center gap-2 text-sm text-white">
          <Sparkle className="size-4 shrink-0" weight="fill" />
          <span className="hidden sm:inline">
            <Trans>Impressed? Create your professional resume in minutes with AI</Trans>
          </span>
          <span className="sm:hidden">
            <Trans>Create your own resume</Trans>
          </span>
          <Link to={ctaUrl}>
            <Button
              size="sm"
              variant="secondary"
              className="ml-2 h-7 bg-white px-3 text-xs font-medium text-blue-600 hover:bg-gray-100"
            >
              {t`Start Basic`}
              <ArrowRight className="ml-1 size-3" />
            </Button>
          </Link>
        </div>
        <button
          className="ml-2 rounded p-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          aria-label={t`Dismiss banner`}
          onClick={handleDismiss}
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Bottom CTA section for public resume pages
 * Always visible, encourages visitors to create their own resume
 */
export const BottomCTASection = ({ templateName }: PublicResumeCTAProps) => {
  const registerUrl = templateName
    ? `/auth/register?source=public-resume&template=${encodeURIComponent(templateName)}`
    : "/auth/register?source=public-resume";

  const builderUrl = templateName
    ? `/builder/try?template=${encodeURIComponent(templateName)}&source=public-resume`
    : "/builder/try?source=public-resume";

  return (
    <div className="mx-auto mb-8 mt-4 max-w-2xl px-4 sm:px-0 print:hidden">
      <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 via-white to-purple-50 shadow-lg dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
        <div className="border-b border-gray-100 bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-1 dark:border-gray-700">
          <div className="flex items-center justify-center gap-2 text-xs font-medium text-white">
            <Sparkle className="size-3" weight="fill" />
            <Trans>AI-Powered Resume Builder</Trans>
          </div>
        </div>

        <div className="p-6 text-center">
          <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">
            <Trans>Ready to build your own standout resume?</Trans>
          </h3>
          <p className="mb-6 text-sm text-gray-600 dark:text-gray-400 sm:text-base">
            <Trans>Join 5 million+ professionals using AI to land interviews faster</Trans>
          </p>

          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <Link to={registerUrl}>
              <Button
                size="lg"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 font-medium text-white shadow-md hover:from-blue-700 hover:to-purple-700 sm:w-auto"
              >
                <Sparkle className="mr-2 size-4" />
                {t`Create Free Resume`}
              </Button>
            </Link>

            {templateName && (
              <Link to={builderUrl}>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full border-gray-300 font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800 sm:w-auto"
                >
                  {t`Use This Template`}
                  <ArrowRight className="ml-2 size-4" />
                </Button>
              </Link>
            )}
          </div>

          <p className="mt-4 text-xs text-gray-500 dark:text-gray-500">
            <Trans>No credit card required. Start building in seconds.</Trans>
          </p>
        </div>
      </div>
    </div>
  );
};
